import React, { useEffect, useState } from 'react';
import { Card } from '@consta/uikit/Card';
import { Button } from '@consta/uikit/Button';
import { Badge } from '@consta/uikit/Badge';
import { Text } from '@consta/uikit/Text';
import { IconArrowUp } from '@consta/icons/IconArrowUp';
import { IconArrowDown } from '@consta/icons/IconArrowDown';
import { useLazyGetDepositQuery } from '../../../../services/depositService';
import { useLazyGetAreaQuery } from '../../../../services/areaService';
import { useLazyGetHorizonQuery } from '../../../../services/horizonService';
import { useLazyGetLayerQuery } from '../../../../services/layerService';
import { useLazyGetCompanyQuery, useLazyGetCompanyUuidQuery } from '../../../../services/companyService';
import { useLazyGetReservoirQuery } from '../../../../services/reservoirService';
import { useLazyGetFieldQuery, useLazyGetFieldUuidQuery } from '../../../../services/fieldService';
import { useParams } from 'react-router-dom';

import classes from './style/FieldInfoPage.module.scss';

interface FieldInfoPageProps {
    selectedField: any;
    depositsVisible: { [key: string]: boolean };
    sectionsVisible: { [key: string]: boolean };
    horizonsVisible: { [key: string]: boolean };
    toggleDepositVisibility: (depositId: string) => void;
    toggleSectionVisibility: (sectionId: string) => void;
    toggleHorizonVisibility: (horizonId: string) => void;
}

const FieldInfoPage: React.FC<FieldInfoPageProps> = ({
    depositsVisible,
    sectionsVisible,
    horizonsVisible,
    toggleDepositVisibility,
    toggleSectionVisibility,
    toggleHorizonVisibility
}) => {
    // Lazy запросы для получения данных
    const [triggerGetDeposits, { data: depositsData }] = useLazyGetDepositQuery();
    const [triggerGetAreas, { data: areasData }] = useLazyGetAreaQuery();
    const [triggerGetHorizons, { data: horizonsData }] = useLazyGetHorizonQuery();
    const [triggerGetLayers, { data: layersData }] = useLazyGetLayerQuery();
    const [triggerGetCompanies, { data: companiesData }] = useLazyGetCompanyQuery();
    const [triggerGetCompanyByUuid] = useLazyGetCompanyUuidQuery();
    const [triggerGetReservoirs, { data: reservoirsData }] = useLazyGetReservoirQuery();
    const [triggerGetFields, { data: fieldsData }] = useLazyGetFieldQuery();
    const [triggerGetFieldByUuid, { data: fieldData }] = useLazyGetFieldUuidQuery();

    // Состояния для компаний месторождения и залежей
    const [selectedField, setSelectedField] = useState<any>(null);
    const [fieldCompanies, setFieldCompanies] = useState<any[]>([]);
    const [depositCompanies, setDepositCompanies] = useState<{[key: string]: any}>({});

    const { fieldUuid } = useParams<{ fieldUuid: string }>();
    
    // Загрузка данных месторождения по UUID из URL
    useEffect(() => {
        if (fieldUuid) {
            loadFieldData(fieldUuid);
        }
    }, [fieldUuid]);

    const loadFieldData = async (uuid: string) => {
        try {
            // Загружаем данные месторождения
            const fieldResult = await triggerGetFieldByUuid({ uuid }).unwrap();
            setSelectedField(fieldResult);

            // Загружаем все остальные данные
            await loadAllData(fieldResult);
            
        } catch (error) {
            console.error('Ошибка загрузки данных месторождения:', error);
        }
    };

    const loadAllData = async (field: any) => {
        if (!field) return;

        try {
            // Загружаем все необходимые данные параллельно
            await Promise.all([
                triggerGetCompanies({ limit: 100, offset: 0, ordering: 'name' }),
                triggerGetReservoirs({}),
                //@ts-ignore
                triggerGetFields({ limit: 100, offset: 0, ordering: 'name' }),
                //@ts-ignore
                triggerGetDeposits({ limit: 100, offset: 0, ordering: 'name' }),
                //@ts-ignore
                triggerGetAreas({ limit: 100, offset: 0, ordering: 'name' }),
                //@ts-ignore
                triggerGetHorizons({ limit: 100, offset: 0, ordering: 'code' }),
                //@ts-ignore
                triggerGetLayers({ limit: 100, offset: 0, ordering: 'name' })
            ]);

            // Загружаем компании для месторождения
            await loadFieldCompanies(field);
            
            // Загружаем компании для залежей
            await loadDepositCompanies();

        } catch (error) {
            console.error('Ошибка загрузки данных:', error);
        }
    };

    // Загрузка компаний для месторождения
    const loadFieldCompanies = async (field: any) => {
        if (!field.companies || !Array.isArray(field.companies)) return;

        try {
            const companiesPromises = field.companies.map(async (companyUuid: string) => {
                const result = await triggerGetCompanyByUuid({ uuid: companyUuid }).unwrap();
                return result;
            });

            const companies = await Promise.all(companiesPromises);
            setFieldCompanies(companies.filter(Boolean));
        } catch (error) {
            console.error('Ошибка загрузки компаний месторождения:', error);
        }
    };

    // Загрузка компаний для залежей
    const loadDepositCompanies = async () => {
        if (!depositsData?.results) return;

        try {
            const depositCompaniesMap: {[key: string]: any} = {};
            const depositsWithCompanies = depositsData.results.filter(deposit => deposit?.companies && Array.isArray(deposit?.companies));
            
            // Для каждой залежи загружаем компании
            for (const deposit of depositsWithCompanies) {
                try {
                    const companiesPromises = deposit?.companies.map(async (companyUuid: string) => {
                        const result = await triggerGetCompanyByUuid({ uuid: companyUuid }).unwrap();
                        return result;
                    });

                    const companies = await Promise.all(companiesPromises);
                    depositCompaniesMap[deposit.uuid] = companies.filter(Boolean);
                } catch (error) {
                    console.error(`Ошибка загрузки компаний для залежи ${deposit.uuid}:`, error);
                }
            }

            setDepositCompanies(depositCompaniesMap);
        } catch (error) {
            console.error('Ошибка загрузки компаний залежей:', error);
        }
    };

    // Простые функции для получения названий
    const getFieldName = (fieldUuid: string) => {
        return fieldsData?.results?.find(f => f.uuid === fieldUuid)?.name || 'Неизвестное месторождение';
    };

    const getDepositName = (depositUuid: string) => {
        return depositsData?.results?.find(d => d.uuid === depositUuid)?.name || 'Неизвестная залежь';
    };

    const getAreaName = (areaUuid: string) => {
        return areasData?.results?.find(a => a.uuid === areaUuid)?.name || 'Неизвестный участок';
    };

    const getHorizonName = (horizonUuid: string) => {
        return horizonsData?.results?.find(h => h.uuid === horizonUuid)?.code || 'Неизвестный горизонт';
    };

    const getCompanyName = (companyUuid: string) => {
        return companiesData?.results?.find(c => c.uuid === companyUuid)?.name || 'Не указана';
    };

    const getReservoirName = (reservoirUuid: string) => {
        const reservoir: any = reservoirsData?.results?.find((r: any) => r.uuid === reservoirUuid);
        return reservoir?.name || 'Не указан';
    };

    // Получение списка компаний месторождения
    const getFieldCompaniesString = () => {
        if (fieldCompanies.length === 0) return 'Не указаны';
        return fieldCompanies.map(company => company.name).join(', ');
    };

    // Получение списка компаний для залежи
    const getDepositCompaniesString = (depositUuid: string) => {
        const companies = depositCompanies[depositUuid];
        if (!companies || companies.length === 0) return 'Не указаны';
        return companies.map((company: any) => company.name).join(', ');
    };

    // Получение данных с фильтрацией
    const getFieldDeposits = () => {
        if (!selectedField) return [];
        return depositsData?.results?.filter(deposit => deposit.field === selectedField.uuid) || [];
    };

    const getDepositAreas = (depositUuid: string) => {
        return areasData?.results?.filter(area => area.deposit === depositUuid) || [];
    };

    const getAreaHorizons = (areaUuid: string) => {
        return horizonsData?.results?.filter(horizon => horizon.area === areaUuid) || [];
    };

    const getHorizonLayers = (horizonUuid: string) => {
        return layersData?.results?.filter(layer => layer.horizon === horizonUuid) || [];
    };

    // Получение типов
    const getFieldType = (type: string) => {
        const types: {[key: string]: string} = {
            'oil': 'Нефтяное',
            'gas': 'Газовое',
            'gas_condensate': 'Газоконденсатное'
        };
        return types[type] || type;
    };

    const getDepositType = (type: string) => {
        const types: {[key: string]: string} = {
            'oil': 'Нефтяная',
            'gas': 'Газовая',
            'gas_condensate': 'Газоконденсатная'
        };
        return types[type] || type;
    };

    if (!selectedField) {
        return (
            <div className={classes.container}>
                <Text>Загрузка данных месторождения...</Text>
            </div>
        );
    }

    const fieldDeposits = getFieldDeposits();

    // Рендер-функции
    const renderDetailedDeposits = () => (
        <div className={classes.depositsSection}>
            <Text size="xl" weight="bold" className={classes.marginBottomLarge}>
                Залежи ({fieldDeposits.length})
            </Text>
            {fieldDeposits.map(deposit => (
                <Card key={deposit.uuid} verticalSpace="m" horizontalSpace="m" className={classes.depositCard}>
                    {!depositsVisible[deposit.uuid] ? (
                        <div className={classes.depositHeader}>
                            <div className={classes.depositHeaderCollapsed}>
                                <Button
                                    iconLeft={IconArrowDown}
                                    onlyIcon
                                    view="clear"
                                    size="xs"
                                    onClick={() => toggleDepositVisibility(deposit.uuid)}
                                />
                                <Text size="l" weight="semibold">{deposit.name}</Text>
                                <Badge size="s" status="normal" label={`Участков: ${getDepositAreas(deposit.uuid).length}`} />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className={classes.depositHeaderExpanded}>
                                <div>
                                    <div className={classes.depositTitleRow}>
                                        <Button
                                            iconLeft={IconArrowUp}
                                            onlyIcon
                                            view="clear"
                                            size="xs"
                                            onClick={() => toggleDepositVisibility(deposit.uuid)}
                                        />
                                        <Text size="l" weight="bold">{deposit.name}</Text>
                                    </div>
                                    
                                    <div className={classes.depositGrid}>
                                        <div>
                                            <Text size="s" view="secondary">Тип:</Text>
                                            <Text size="s">{deposit?.type_name}</Text>
                                        </div>
                                        <div>
                                            <Text size="s" view="secondary">Месторождение:</Text>
                                            <Text size="s">{getFieldName(deposit.field)}</Text>
                                        </div>
                                        <div>
                                            <Text size="s" view="secondary">Компании:</Text>
                                            <Text size="s">{deposit?.companies?.map((company: any) => company.name).join(', ')}</Text>
                                        </div>
                                        <div>
                                            <Text size="s" view="secondary">Фонд добывающих:</Text>
                                            <Text size="s">{deposit.total_producing_wells || 'Не указан'}</Text>
                                        </div>
                                        <div>
                                            <Text size="s" view="secondary">Фонд нагнетательных:</Text>
                                            <Text size="s">{deposit.total_injection_wells || 'Не указан'}</Text>
                                        </div>
                                    </div>
                                    {deposit.description && (
                                        <div>
                                            <Text size="s" view="secondary">Описание:</Text>
                                            <Text size="s">{deposit.description}</Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {renderDetailedAreas(deposit)}
                        </div>
                    )}
                </Card>
            ))}
        </div>
    );

    const renderDetailedAreas = (deposit: any) => {
        const areas = getDepositAreas(deposit.uuid);
        
        return (
            <div className={classes.areasSection}>
                <Text size="m" weight="semibold" className={classes.marginBottomMedium}>
                    Участки ({areas.length})
                </Text>
                {areas.map(area => (
                    <Card key={area.uuid} verticalSpace="m" horizontalSpace="m" className={classes.areaCard}>
                        {!sectionsVisible[area.uuid] ? (
                            <div className={classes.areaHeaderCollapsed}>
                                <div className={classes.areaHeaderContent}>
                                    <Button
                                        iconLeft={IconArrowDown}
                                        onlyIcon
                                        view="clear"
                                        size="xs"
                                        onClick={() => toggleSectionVisibility(area.uuid)}
                                    />
                                    <Text size="m" weight="semibold">{area.name}</Text>
                                    <Badge size="s" status="system" label={`Горизонтов: ${getAreaHorizons(area.uuid).length}`} />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className={classes.marginBottomMedium}>
                                    <div className={classes.areaTitleRow}>
                                        <Button
                                            iconLeft={IconArrowUp}
                                            onlyIcon
                                            view="clear"
                                            size="xs"
                                            onClick={() => toggleSectionVisibility(area.uuid)}
                                        />
                                        <Text size="m" weight="semibold">{area.name}</Text>
                                    </div>
                                    
                                    <div className={classes.areaGrid}>
                                        <div>
                                            <Text size="xs" view="secondary">Компания:</Text>
                                            <Text size="xs">{getCompanyName(area.company)}</Text>
                                        </div>
                                        <div>
                                            <Text size="xs" view="secondary">Залежь:</Text>
                                            <Text size="xs">{getDepositName(area.deposit)}</Text>
                                        </div>
                                        <div>
                                            <Text size="xs" view="secondary">Фонд добывающих:</Text>
                                            <Text size="xs">{area.total_producing_wells || 'Не указан'}</Text>
                                        </div>
                                        <div>
                                            <Text size="xs" view="secondary">Фонд нагнетательных:</Text>
                                            <Text size="xs">{area.total_injection_wells || 'Не указан'}</Text>
                                        </div>
                                    </div>
                                    {area.description && (
                                        <div>
                                            <Text size="xs" view="secondary">Описание:</Text>
                                            <Text size="xs">{area.description}</Text>
                                        </div>
                                    )}
                                </div>
                                {renderDetailedHorizons(area)}
                            </div>
                        )}
                    </Card>
                ))}
            </div>
        );
    };

    const renderDetailedHorizons = (area: any) => {
        const horizons = getAreaHorizons(area.uuid);
        
        return (
            <div className={classes.horizonsSection}>
                <Text size="s" weight="semibold" className={classes.marginBottomSmall}>
                    Горизонты ({horizons.length})
                </Text>
                {horizons.map(horizon => (
                    <Card key={horizon.uuid} verticalSpace="s" horizontalSpace="s" className={classes.horizonCard}>
                        {!horizonsVisible[horizon.uuid] ? (
                            <div className={classes.horizonHeaderCollapsed}>
                                <div className={classes.horizonHeaderContent}>
                                    <Button
                                        iconLeft={IconArrowDown}
                                        onlyIcon
                                        view="clear"
                                        size="xs"
                                        onClick={() => toggleHorizonVisibility(horizon.uuid)}
                                    />
                                    <Text size="s" weight="semibold">{horizon.code}</Text>
                                    <Badge size="s" status="warning" label={`Пластов: ${getHorizonLayers(horizon.uuid).length}`} />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className={classes.marginBottomSmall}>
                                    <div className={classes.horizonTitleRow}>
                                        <Button
                                            iconLeft={IconArrowUp}
                                            onlyIcon
                                            view="clear"
                                            size="xs"
                                            onClick={() => toggleHorizonVisibility(horizon.uuid)}
                                        />
                                        <Text size="s" weight="semibold">{horizon.code}</Text>
                                    </div>
                                    
                                    <div className={classes.horizonGrid}>
                                        <div>
                                            <Text size="xs" view="secondary">Участок:</Text>
                                            <Text size="xs">{getAreaName(horizon.area)}</Text>
                                        </div>
                                        <div>
                                            <Text size="xs" view="secondary">Глубина залегания:</Text>
                                            <Text size="xs">{horizon.burial_depth ? `${horizon.burial_depth} м` : 'Не указана'}</Text>
                                        </div>
                                    </div>
                                    {horizon.description && (
                                        <div>
                                            <Text size="xs" view="secondary">Описание:</Text>
                                            <Text size="xs">{horizon.description}</Text>
                                        </div>
                                    )}
                                </div>
                                {renderDetailedLayers(horizon)}
                            </div>
                        )}
                    </Card>
                ))}
            </div>
        );
    };

    const renderDetailedLayers = (horizon: any) => {
        const layers = getHorizonLayers(horizon.uuid);
        
        return (
            <div className={classes.layersSection}>
                <Text size="xs" weight="semibold" className={classes.marginBottomSmall}>
                    Пласты ({layers.length})
                </Text>
                {layers.map(layer => (
                    <div key={layer.uuid} className={classes.layerItem}>
                        <Text size="xs" weight="semibold" className={classes.layerTitle}>{layer.name}</Text>
                        
                        <div className={classes.layerGrid}>
                            <div>
                                <Text size="xs" view="secondary">Горизонт:</Text>
                                <Text size="xs">{getHorizonName(layer.horizon)}</Text>
                            </div>
                            <div>
                                <Text size="xs" view="secondary">Глубина залегания:</Text>
                                <Text size="xs">{layer.burial_depth ? `${layer.burial_depth} м` : 'Не указана'}</Text>
                            </div>
                            <div>
                                <Text size="xs" view="secondary">Пористость:</Text>
                                <Text size="xs">{layer.porosity ? `${layer.porosity}д.ед` : 'Не указана'}</Text>
                            </div>
                            <div>
                                <Text size="xs" view="secondary">Проницаемость:</Text>
                                <Text size="xs">{layer.permeability ? `${layer.permeability} мкм²` : 'Не указана'}</Text>
                            </div>
                            <div>
                                <Text size="xs" view="secondary">Толщина пласта:</Text>
                                <Text size="xs">{layer.formation_thickness ? `${layer.formation_thickness} м` : 'Не указана'}</Text>
                            </div>
                            <div>
                                <Text size="xs" view="secondary">Тип коллектора:</Text>
                                <Text size="xs">{getReservoirName(layer.reservoir)}</Text>
                            </div>
                        </div>
                        {layer.description && (
                            <div className={classes.layerDescription}>
                                <Text size="xs" view="secondary">Описание:</Text>
                                <Text size="xs">{layer.description}</Text>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className={classes.container}>
            <div className={classes.breadcrumb}>
                <Text size="s" view="secondary">
                    Месторождения / {selectedField.name}
                </Text>
            </div>

            <div className={classes.title}>
                <Text size="2xl" weight="bold">
                    Месторождение - {selectedField.name}
                </Text>
            </div>

            <div className={classes.badgesContainer}>
                <Badge 
                    size="s" 
                    status="system" 
                    view='stroked'
                    form='round'
                    label={`Тип: ${getFieldType(selectedField.type_name)}`} 
                />
                <Badge 
                    size="s" 
                    status="system" 
                    view='stroked'
                    form='round'
                    label={`Стадия: ${selectedField.current_stage || 'Не указана'}`} 
                />
                <Badge 
                    size="s" 
                    status="system" 
                    view='stroked'
                    form='round'
                    label={`Компании: ${selectedField?.companies?.map((company: any) => company.name).join(', ')}`} 
                />
                <Badge 
                    size="s" 
                    status="system" 
                    view='stroked'
                    form='round'
                    label={`Регион: ${selectedField.geographic_region || 'Не указан'}`} 
                />
            </div>

            <div className={classes.mainLayout}>
                <div className={classes.sidebar}>
                    <Card verticalSpace="l" horizontalSpace="l" className={classes.infoCard}>
                        <div className={classes.marginBottomLarge}>
                            <Text size="xl" weight="bold" className={classes.infoTitle}>
                                Основная информация
                            </Text>
                            <div className={classes.infoGrid}>
                                <div className={classes.infoItem}>
                                    <Text size="s" view="secondary">Тип</Text>
                                    <Text>{getFieldType(selectedField.type_name)}</Text>
                                </div>
                                <div className={classes.infoItem}>
                                    <Text size="s" view="secondary">Стадия</Text>
                                    <Text>{selectedField.current_stage || 'Не указана'}</Text>
                                </div>
                                <div className={classes.infoItem}>
                                    <Text size="s" view="secondary">Компании</Text>
                                    <Text>{selectedField?.companies?.map((company: any) => company.name).join(', ')}</Text>
                                </div>
                                <div className={classes.infoItem}>
                                    <Text size="s" view="secondary">Географический район</Text>
                                    <Text>{selectedField.geographic_region || 'Не указан'}</Text>
                                </div>
                                <div className={classes.infoItem}>
                                    <Text size="s" view="secondary">Фонд добывающих скважин</Text>
                                    <Text>{selectedField.total_producing_wells || 'Не указан'}</Text>
                                </div>
                                <div className={classes.infoItem}>
                                    <Text size="s" view="secondary">Фонд нагнетательных скважин</Text>
                                    <Text>{selectedField.total_injection_wells || 'Не указан'}</Text>
                                </div>
                                <div className={classes.depositsCount}>
                                    <Text size="s" view="secondary">Количество залежей</Text>
                                    <Text size="l" weight="bold">{fieldDeposits.length}</Text>
                                </div>
                            </div>
                        </div>

                        {selectedField.description && (
                            <Card verticalSpace="m" horizontalSpace="m" className={classes.descriptionCard}>
                                <Text size="m" weight="semibold" className={classes.marginBottomSmall}>
                                    Описание
                                </Text>
                                <Text>{selectedField.description}</Text>
                            </Card>
                        )}
                    </Card>
                </div>

                <div className={classes.mainContent}>
                    <div className={classes.statsRow}>
                        <Card verticalSpace="l" horizontalSpace="l" className={classes.statCard}>
                            <Text size="m" weight="semibold" className={classes.statValue}>
                                Количество залежей
                            </Text>
                            <Text size="2xl" weight="bold" view="primary">
                                {fieldDeposits.length}
                            </Text>
                        </Card>
                        <Card verticalSpace="l" horizontalSpace="l" className={classes.statCard}>
                            <Text size="m" weight="semibold" className={classes.statValue}>
                                Общее количество участков
                            </Text>
                            <Text size="2xl" weight="bold" view="primary">
                                {fieldDeposits.reduce((total, deposit) => total + getDepositAreas(deposit.uuid).length, 0)}
                            </Text>
                        </Card>
                    </div>
                    
                    {renderDetailedDeposits()}
                </div>
            </div>
        </div>
    );
};

export default FieldInfoPage;